import { Controller } from "@hotwired/stimulus";
import LostReceiptsWorkflow from "../../bank_transactions/lost_receipts_workflow";
import { registerFormattedDateHelper, registerNumberToCurrencyHelper } from "../../utils/handlebars_helpers";
import { compiledTemplate } from "../../utils/string_utils";
import $ from "jquery";
import { errorMessages, notifyError } from "../../components/flash_alert";
import { toggleClass } from "../../utils/misc";
import { postAjaxRequest } from "../../utils/http_utils";

export default class extends Controller {
  static values = { company: Object, initialTransactions: Array, urls: Object };
  static targets = [
    "step",
    "transactionsNotAssignedMessage",
    "shareholdersSelectionTemplate",
    "chargeSelectionTemplate",
    "confirmationTemplate",
    "summaryContainer",
    "previousStepLink",
    "loader",
    "shareholderAccountAssignmentInput",
    "chargeAssignmentInput",
  ];

  initialize() {
    this.currentStep = 0;
    this.workflow = new LostReceiptsWorkflow(this.companyValue, this.initialTransactionsValue);

    registerNumberToCurrencyHelper();
    registerFormattedDateHelper();
  }

  nextStep() {
    this._displayNewStep(this.currentStep, this.currentStep + 1);
    this.currentStep += 1;
    this._togglePreviousStepLink();
  }

  previousStep() {
    this._displayNewStep(this.currentStep, this.currentStep - 1);
    this.currentStep -= 1;
    this._togglePreviousStepLink();
  }

  assignAll(e) {
    const selectedAssignment = e.currentTarget.value;
    const allocationInputs =
      selectedAssignment === "charge"
        ? this.chargeAssignmentInputTargets
        : this.shareholderAccountAssignmentInputTargets;
    allocationInputs.forEach((input) => {
      input.checked = true;
      const { transactionId } = input.dataset;
      this.workflow.transactionAssignmentUpdated(transactionId, selectedAssignment);
    });

    this._checkTransactionsAssignmentValidity();
  }

  _togglePreviousStepLink() {
    toggleClass(this.previousStepLinkTarget, "hidden", this.currentStep !== 0);
  }

  validateAssignment() {
    if (!this.workflow.allTransactionsAssigned()) {
      this.transactionsNotAssignedMessageTarget.classList.remove("hidden");
      return;
    }

    if (this.workflow.hasMultipleShareholders()) {
      this._displayOptionalSteps();
    } else {
      this._displayLoader();
      this._postShareholderSelection(() => this._displayOptionalSteps());
    }
  }

  transactionAssignmentUpdated(e) {
    const { transactionId } = e.target.dataset;
    this.workflow.transactionAssignmentUpdated(transactionId, e.target.value);

    this._checkTransactionsAssignmentValidity();
  }

  transactionShareholderDefined(e) {
    const { transactionId } = e.target.dataset;
    this.workflow.transactionShareholderDefined(transactionId, e.target.value);
  }

  validateShareholdersSelection(e) {
    e.preventDefault();

    if (!this._isFormValid(e.currentTarget.form)) {
      return;
    }

    this._displayLoader();
    this._postShareholderSelection(() => {
      if (this.workflow.shouldDefineCharges()) {
        this._displayChargeSelectionStep();
      } else {
        this._displaySummaryStep();
      }
    });
  }

  transactionChargeDefined(e) {
    const { transactionId } = e.target.dataset;
    this.workflow.transactionChargeDefined(transactionId, e.target.value);
  }

  validateChargesSelection(e) {
    e.preventDefault();

    if (!this._isFormValid(e.currentTarget.form)) {
      return;
    }

    this._displayLoader();
    this._postChargeSelection();
  }

  _postChargeSelection() {
    this._postData({
      url: this.urlsValue.charges,
      body: this.workflow.chargeTransactionsParams(),
      onSuccess: () => this._displaySummaryStep(),
    });
  }

  transactionsValidated() {
    document.dispatchEvent(new CustomEvent("bankTransactions:lostReceiptsConfirmed"));
  }

  _stepFor(stepNumber) {
    return this.stepTargets.find((target, index) => index === stepNumber);
  }

  _displayNewStep(currentStepNumber, nextStepNumber) {
    this._stepFor(currentStepNumber).classList.remove("current");
    this._stepFor(nextStepNumber).classList.add("current");
  }

  _totalAmountFor(transactions) {
    const sum = transactions.reduce((accumulator, currentTransaction) => accumulator - currentTransaction.value, 0);
    return sum;
  }

  _displayShareholderSelectionStep() {
    const templateContent = compiledTemplate(this.shareholdersSelectionTemplateTarget, {
      bank_transactions: this.workflow.shareholderAccountTransactions(),
    });
    this._retrieveCurrentStep().insertAdjacentHTML("afterend", templateContent);
    this.nextStep();
  }

  _retrieveCurrentStep() {
    return this.stepTargets[this.currentStep];
  }

  _displaySummaryStep() {
    const shareholdersTransactions = this.workflow.shareholderAccountTransactions();
    const shareholdersTransactionsSum = this._totalAmountFor(shareholdersTransactions);
    const chargesTransactions = this.workflow.chargeTransactions();
    const chargesTransactionsSum = this._totalAmountFor(chargesTransactions);
    const options = {
      lostReceiptsSum: shareholdersTransactionsSum + chargesTransactionsSum,
      numberOfLostReceipts: shareholdersTransactions.length + chargesTransactions.length,
      shareholdersLostReceiptsSum: shareholdersTransactionsSum,
      chargesLostReceiptsSum: chargesTransactionsSum,
    };
    const templateContent = compiledTemplate(this.confirmationTemplateTarget, options);
    this.summaryContainerTarget.innerHTML = templateContent;
    this.nextStep();
  }

  _resetOptionalSteps() {
    this._shareholderSelectionStep()?.remove();
    this._chargeSelectionStep()?.remove();
  }

  _chargeSelectionStep() {
    return this.stepTargets.find((step) => step.dataset.stepName === "charge-selection");
  }

  _shareholderSelectionStep() {
    return this.stepTargets.find((step) => step.dataset.stepName === "shareholder-selection");
  }

  _displayChargeSelectionStep() {
    this._chargeSelectionStep()?.remove();
    const templateContent = compiledTemplate(this.chargeSelectionTemplateTarget, {
      bank_transactions: this.workflow.chargeTransactions(),
    });
    this._retrieveCurrentStep().insertAdjacentHTML("afterend", templateContent);
    this.nextStep();
  }

  _postShareholderSelection(onSuccess) {
    this._postData({
      url: this.urlsValue.shareholders,
      body: this.workflow.shareholderAccountTransactionsParams(),
      onSuccess: onSuccess,
    });
  }

  _postData(args) {
    postAjaxRequest(args.url, args.body).then((response) => {
      if (response.status === 200) {
        args.onSuccess();
      } else {
        notifyError(errorMessages(response.status));
      }

      this._hideLoader();
    });
  }

  _displayLoader() {
    this.loaderTarget.classList.remove("hidden");
  }

  _hideLoader() {
    this.loaderTarget.classList.add("hidden");
  }

  _displayOptionalSteps() {
    this._resetOptionalSteps();

    if (this.workflow.shouldDefineShareholders()) {
      this._displayShareholderSelectionStep();
    } else if (this.workflow.shouldDefineCharges()) {
      this._displayChargeSelectionStep();
    } else {
      this._displaySummaryStep();
    }
  }

  _checkTransactionsAssignmentValidity() {
    if (this.workflow.allTransactionsAssigned()) {
      this.transactionsNotAssignedMessageTarget.classList.add("hidden");
    }
  }

  _isFormValid(form) {
    return $(form).parsley().validate();
  }
}
